// Deterministic supply-house price-sheet extraction.
//
// A price list PDF has a real text layer. Rendering it to an image and asking a
// vision model to read it back throws away exact numbers and reintroduces OCR
// risk on a document that never needed it. This reads the text layer directly.
//
// It also captures the thing that matters most and that the vision path never
// asked for: the PER / UOM column. Supply houses quote small parts per 100 (C)
// and wire per 1,000 (M), so a sheet read without it stores 12/2 Romex at
// $635.94 PER FOOT instead of $0.63594 - 1000x, silently.
//
// Nothing here is specific to one supplier. Columns are DISCOVERED: cluster
// text by X position, then profile each column by what it contains.
// Validated by reproducing a known-good hand-written CED parse exactly:
// 2,484 of 2,484 rows, 0 missing, 0 extra.

const MONEY = /^\$?\s*[\d,]+\.\d{2}$/;
const UNIT  = /^(E|EA|C|M|FT|LF|BX|PK|RL|CX|PC|HU|TH|MFT|CLF|H87|C62)$/i;
const UPC   = /^\d{11,14}$/;

// Basis QUANTITY from the supplier's code. C is centum (100), M is mille
// (1,000) - Roman numerals, and established trade shorthand with no normative
// standard, which is exactly why it has to be read off the sheet and never
// guessed.
export const basisQty = code => {
  const u = String(code || '').trim().toUpperCase();
  if (u === 'C' || u === 'HU' || u === 'CLF') return 100;
  if (u === 'M' || u === 'TH' || u === 'MFT') return 1000;
  return 1;
};

// The basis UNIT is decided from the ITEM, never from the letter: for piece
// goods /C means per 100 PIECES, for linear goods it means per 100 FEET.
// PIECE deliberately wins over LINEAR, because a "4in EMT CONN" contains EMT
// but is a fitting sold singly.
const LINEAR = new RegExp([
  '\\b(EMT|IMC|RIGID|GRC|CONDUIT|ENT|LIQUIDTIGHT|SEALTITE|ROMEX|NM-?\\s?B|NMB|THHN|THWN|XHHW|UF-?B',
  '|SER|SEU|SEOW|SEOOW|SOOW|SJOOW|SJTW|SJEOW|SJEO|SJT|TRAY\\s?CABLE|RACEWAY|UNISTRUT|STRUT|WIREMOLD',
  '|L\\/T|FLEX|COAX|CAT\\s?-?[56]E?|THERMOSTAT\\s+WIRE)S?\\b',
  '|\\d\\s*MCM',
  '|\\b\\d+\\s*AWG\\b',
  '|#\\s*\\d+\\s*(STR|SOL|SOLID|STRANDED)\\b',
  '|\\b(STR|SOL|SOLID)\\s+BARE\\b|\\bBARE\\s+(COPPER|CU)\\b',
  '|\\bMC-?(AL|CU)?-?\\d',
  '|(SEOW|SEOOW|SOOW|SJOOW|SJEOW|SJEO|SJTW|SJT|SEU|SER|THHN|THWN|XHHW|UFB)\\s*\\d',
  '|\\b\\d+\\/\\d+\\s*(W\\/?G|WG|\\+)',
  '|\\b\\d+\\/\\d+\\s*(SEOW|SJEOW|SJEO|SJ|CORD|SHIELDED|STRANDED|PR\\b)',
  '|\\b(REEL|COIL|SPOOL|RL|RLS)S?\\b',
  "|\\d\\s*'(?!')",   // a single prime is feet; '' is inches, not a length
  '|\\b\\d+\\s*FT\\b',
].join(''), 'i');

const PIECE = /\b(CONN|CONNECTOR|CPL|CPLG|COUPLING|STRAP|ELBOW|ELL|EL|BUSHING|BUSHNG|BUSHED|BUSH|LB|LL|LR|TEE|FITTING|HANGER|CLAMP|BOX|COVER|CVR|PLATE|RING|NIPPLE|ADAPTER|ADPT|CAP|PLUG|HUB|KIT|NUT|WIRENUT|WN|TWISTCONN|SCREW|SCR|ANCHOR|STACKER|SUPPORT|SUPP|BRACKET|BRKT|CLIP|LUG|TERMINAL|TERMINATOR|BREAKER|PANEL|LOADCENTER|SWITCH|TOGGLE|RECEPT|RECEPS|RECEP|RCPT|RECEPTACLE|DUPLEX|OUTLET|GFCI|GFI|DIMMER|FIXTURE|LAMP|BULB|WAFER|SCONCE|TAPE|MASK|CAULK|CEMENT|BIT|BLADE|SAW|TOOL|PLIER|DRIVER|METER|TESTER|SHRINK|PACK|MARKER|LABEL|STRIP|BLANK|FLSHING|FLASHING|ATTACHMENT|COMBO|ROUND|SQUARE|SQ|PAN|PANCAKE|CAN|TRIM|GANG|MUD|PLASTER|PLASTIC|PLSTC|KO|KNOCKOUT|SEAL|WHIP|SLEEVE|GROMMET|WASHER|WASH|WSHR|FENDER|BOLT|STUD|ROD|CHANNEL|PULL\s?BOX|HOOK|EYE|STAPLE|TIE|WRAP|LOCKNUT|GRIP|SADDLE|FUSE|CB|PROTR|COND|DIE\s?CAST|TEK|TAPCN|TAPCON|PROCON|K-LATH|DRYWALL|PEANUT|PIN|PIGTAIL|SPLCE|SPLICE|FORK|FEMALE|MALE|CONDULET|JB|DEVICE|DEV|EXTENSION|SECTION|ASSEMBLY|END|HOLE|TAB|KICK)S?\b/i;

export function billUnitFor(desc) {
  const d = String(desc || '');
  if (PIECE.test(d)) return { unit: 'ea', why: LINEAR.test(d) ? 'fitting for linear goods' : 'piece goods' };
  if (LINEAR.test(d)) return { unit: 'ft', why: 'linear goods' };
  return { unit: 'ea', why: 'no pattern matched', uncertain: true };
}

// Package size, e.g. "NM12/2WG 250' COILS" -> 250. Purchasing convenience only:
// the canonical cost is per foot, and billing must never touch this. A supply
// house prices per 1,000 ft whatever coil is on the shelf.
export function packageOf(desc, unit) {
  if (unit !== 'ft') return null;
  // \d{2,4} missed a DECIMAL package size, so the 10-metre COB rolls printed
  // as "32.8'" came back with no package at all and their roll price was left
  // sitting in a per-foot field. And '' is INCHES, not feet - "40'' PRO SWIVEL"
  // was read as a 40 FOOT package. The lookahead rejects the double prime.
  const m = String(desc).match(/(\d{2,4}(?:\.\d{1,2})?)\s*(?:'(?!')|FT\b|-FT\b)/i);
  if (!m) return null;
  const kind = (String(desc).match(/\b(COIL|REEL|SPOOL|ROLL)S?\b/i) || [])[1];
  return { qty: Number(m[1]), label: Number(m[1]) + ' ft' + (kind ? ' ' + kind.toLowerCase() : '') };
}

// Standard stick length. Unlike a coil or a reel this is NOT printed on the
// paper - conduit never says "10 ft" because everyone in the trade knows it.
// Adam, 2026-11-01: "conduit will always be sold at a full 10' stick."
// Only ever consulted for goods the classifier already called LINEAR, which
// is what keeps conduit STRAPS, HANGERS and FITTINGS out of it.
export const STICK_FT = 10;
export function stickOf(desc, unit) {
  if (unit !== 'ft') return null;
  if (!/\b(EMT|IMC|RMC|GRC|RIGID|PVC|CONDUIT|COND)\b/i.test(String(desc || ''))) return null;
  return { qty: STICK_FT, label: STICK_FT + ' ft stick' };
}

// ── the extractor ──────────────────────────────────────────────────────────
export async function extractPriceSheet(pdf, onProgress) {
  const items = [];
  for (let p = 1; p <= pdf.numPages; p++) {
    if (onProgress) onProgress(`Reading page ${p} of ${pdf.numPages}...`);
    const tc = await (await pdf.getPage(p)).getTextContent();
    for (const it of tc.items) {
      const s = (it.str || '').trim();
      if (s) items.push({ page: p, x: it.transform[4], y: Math.round(it.transform[5] / 2) * 2, s });
    }
  }
  if (items.length < 20) return { ok: false, reason: 'no-text-layer', items: [] };

  // Cluster X positions into columns. Tolerance is generous because a
  // right-aligned numeric column starts at a different X on every row.
  const TOL = 12;
  const xs = items.map(i => i.x).sort((a, b) => a - b);
  const raw = [];
  for (const x of xs) {
    const last = raw[raw.length - 1];
    if (last && x - last.max <= TOL) { last.max = x; last.n++; }
    else raw.push({ min: x, max: x, n: 1 });
  }
  const rowCount = new Set(items.map(i => i.page + ':' + i.y)).size;
  const cols = raw.filter(c => c.n >= rowCount * 0.25).map((c, i) => ({ id: i, min: c.min, max: c.max, n: c.n }));
  if (!cols.length) return { ok: false, reason: 'no-columns', items: [] };
  const colOf = x => {
    let best = null, bestD = Infinity;
    for (const c of cols) {
      const d = x < c.min ? c.min - x : x > c.max ? x - c.max : 0;
      if (d < bestD) { bestD = d; best = c; }
    }
    return best;
  };

  // Profile each column by CONTENT, not by position.
  const prof = cols.map(c => ({ ...c, vals: [] }));
  for (const it of items) { const c = colOf(it.x); if (c) prof[cols.indexOf(c)].vals.push(it.s); }
  for (const p of prof) {
    const n = p.vals.length || 1;
    p.count = p.vals.length;
    p.money = p.vals.filter(v => MONEY.test(v)).length / n;
    p.unit  = p.vals.filter(v => UNIT.test(v)).length / n;
    p.upc   = p.vals.filter(v => UPC.test(v)).length / n;
    p.alnum = p.vals.filter(v => /[A-Za-z]/.test(v) && /\d/.test(v)).length / n;
    p.avgLen = p.vals.reduce((a, v) => a + v.length, 0) / n;
  }

  const best = (f, min) => { const c = prof.filter(p => f(p) >= min).sort((a, b) => f(b) - f(a))[0]; return c || null; };
  const priceCol = best(p => p.money, 0.5);
  const unitCol  = best(p => p.unit, 0.5);
  const upcCol   = best(p => p.upc, 0.5);
  const used = new Set([priceCol, unitCol, upcCol].filter(Boolean).map(p => p.id));
  const descCol = prof.filter(p => !used.has(p.id)).sort((a, b) => b.avgLen - a.avgLen)[0] || null;
  if (descCol) used.add(descCol.id);
  // Catalog number: the mixed letters-and-digits column to the LEFT of the
  // description. It is the stable identity for a material - the same cable
  // appears as NM122WG and NM122WGR, which description text cannot match.
  const catCol = prof.filter(p => !used.has(p.id) && descCol && p.min < descCol.min)
    .sort((a, b) => b.alnum - a.alnum || b.avgLen - a.avgLen)[0] || null;

  if (!priceCol || !unitCol || !descCol) {
    return { ok: false, reason: !priceCol ? 'no-price-column' : !unitCol ? 'no-unit-column' : 'no-description-column',
             columns: prof, items: [] };
  }

  // Re-read rows through the discovered columns.
  const byRow = new Map();
  for (const it of items) {
    const c = colOf(it.x); if (!c) continue;
    const k = it.page + ':' + it.y;
    if (!byRow.has(k)) byRow.set(k, new Map());
    const r = byRow.get(k);
    r.set(c.id, (r.has(c.id) ? r.get(c.id) + ' ' : '') + it.s);
  }

  const out = [], skipped = [];
  for (const [k, r] of byRow) {
    const price = r.get(priceCol.id), unitRaw = r.get(unitCol.id);
    // A long value OVERFLOWS its column: an item is assigned by where it
    // STARTS, so a long catalog number runs across the description column and
    // leaves that cell empty. Fall back to the nearest non-empty column on the
    // left. Eleven CED rows need this, and without it they vanish silently.
    let desc = r.get(descCol.id);
    if (!desc) {
      for (let id = descCol.id - 1; id >= 0; id--) {
        if (upcCol && id === upcCol.id) break;
        if (r.get(id)) { desc = r.get(id); break; }
      }
    }
    if (!desc) continue;
    if (!price || !MONEY.test(String(price).trim())) {
      if (desc.length > 3) skipped.push({ desc: desc.trim(), why: 'no price' });
      continue;
    }
    if (!unitRaw || !UNIT.test(String(unitRaw).trim())) {
      skipped.push({ desc: desc.trim(), why: 'no unit code' });
      continue;
    }
    const label = String(unitRaw).trim().toUpperCase();
    const qty = basisQty(label);
    const srcPrice = Number(String(price).replace(/[$,\s]/g, ''));
    const b = billUnitFor(desc);
    const unitCost = Math.round((srcPrice / qty) * 1e6) / 1e6;
    const pkg = packageOf(desc, b.unit);
    out.push({
      page: Number(k.split(':')[0]),
      desc: desc.trim(),
      cat: catCol ? String(r.get(catCol.id) || '').trim() : '',
      upc: upcCol ? String(r.get(upcCol.id) || '').trim() : '',
      srcPrice, srcLabel: label, basisQty: qty,
      billUnit: b.unit, unitUncertain: !!b.uncertain,
      unitCost,
      pkgQty: pkg ? pkg.qty : null, pkgLabel: pkg ? pkg.label : null,
    });
  }

  // Anything printed on page 1 ABOVE the first priced row, minus the column
  // headings. On a branded sheet that is the letterhead; on this one it is
  // empty, which is the correct answer rather than a wrong guess.
  const COLHEAD = /^(upc|mfr|manufacturer|cat(alog)?\s*#?|description|desc|price|per|qty|quantity|item|unit|uom|cost|list|net|part|sku|#)$/i;
  const firstDataY = out.length ? Math.max(...items.filter(i => i.page === 1).map(i => i.y).filter(y =>
    byRow.has('1:' + y) && byRow.get('1:' + y).has(priceCol.id))) : null;
  const supplierHint = firstDataY == null ? '' : items
    .filter(i => i.page === 1 && i.y > firstDataY && !COLHEAD.test(i.s.trim()))
    .map(i => i.s.trim()).filter(s => s.length > 2 && !/^[\d\s.,$%-]+$/.test(s))
    .join(' ').slice(0, 120).trim();

  return {
    supplierHint,
    ok: out.length > 0,
    reason: out.length ? null : 'no-rows',
    items: out,
    skipped,
    roles: {
      price: priceCol ? { id: priceCol.id, pct: Math.round(priceCol.money * 100) } : null,
      unit:  unitCol  ? { id: unitCol.id,  pct: Math.round(unitCol.unit * 100) } : null,
      desc:  descCol  ? { id: descCol.id,  avgLen: Math.round(descCol.avgLen) } : null,
      cat:   catCol   ? { id: catCol.id,   pct: Math.round(catCol.alnum * 100) } : null,
      upc:   upcCol   ? { id: upcCol.id,   pct: Math.round(upcCol.upc * 100) } : null,
    },
    columns: prof.map(p => ({ id: p.id, count: p.count, money: p.money, unit: p.unit, upc: p.upc, avgLen: p.avgLen })),
    // How many rows are NOT simply per-each. This is the number that says
    // whether getting the basis right mattered on this sheet.
    nonEach: out.filter(r => r.basisQty > 1).length,
  };
}
